import { addDays, endOfWeek, type PreferenceValue, routes, startOfWeek, todayIso } from '@pmai/contracts';
import { Button, Card, EmptyState, IconButton, PageHeader, TextField, FilterSelect } from '@pmai/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ProjectStatusBadge } from '../../entities/projects/project-badges';
import { TaskDialog } from '../../entities/tasks/task-dialog';
import { TaskStatusSelect } from '../../entities/tasks/task-filters';
import { TaskSummaryLine, TasksTable } from '../../entities/tasks/tasks-table';
import { useApiQuery } from '../../shared/api/hooks';
import { Can } from '../../shared/auth/session';
import { usePreference } from '../../shared/preferences';

type Filters = PreferenceValue<'tasksFilters'>;

/** Never saved → the current week (Monday–Sunday), every status, everyone (FR-TASK-011). */
const defaultFilters = (): Filters => {
  const today = todayIso();
  return { from: startOfWeek(today), to: endOfWeek(today), status: 'all', assigneeId: null };
};

/** All tasks by deadline period (FR-TASK-010…020). */
export function TasksPage() {
  const { t } = useTranslation();
  const scope = { kind: 'all' } as const;
  const pref = usePreference('tasksFilters');
  const filters = pref.value ?? defaultFilters();
  const set = (patch: Partial<Filters>) => pref.save({ ...filters, ...patch });
  const shift = (days: number) =>
    filters.from && filters.to && set({ from: addDays(filters.from, days), to: addDays(filters.to, days) });
  const resources = useApiQuery(routes.resources.list);
  const tasks = useApiQuery(
    routes.tasks.list,
    {
      query: {
        from: filters.from ?? undefined,
        to: filters.to ?? undefined,
        status: filters.status,
        assigneeId: filters.assigneeId ?? undefined,
        order: 'deadline',
      },
    },
    { enabled: pref.loaded, placeholderData: (previous) => previous },
  );
  const [adding, setAdding] = useState(false);
  const bothDates = !!filters.from && !!filters.to;

  return (
    <>
      <PageHeader
        title={t('tasks.title')}
        actions={
          <Can perm="tasks.edit">
            <Button icon="plus" onClick={() => setAdding(true)}>
              {t('tasks.addTask')}
            </Button>
          </Can>
        }
      />
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-end gap-3">
          <IconButton
            icon="chevron-left"
            variant="secondary"
            label={t('tasks.prevWeek')}
            disabled={!bothDates}
            onClick={() => shift(-7)}
          />
          <TextField
            label={t('tasks.from')}
            type="date"
            value={filters.from ?? ''}
            onChange={(e) => set({ from: e.target.value || null })}
          />
          <TextField
            label={t('tasks.to')}
            type="date"
            value={filters.to ?? ''}
            onChange={(e) => set({ to: e.target.value || null })}
          />
          <IconButton
            icon="chevron-right"
            variant="secondary"
            label={t('tasks.nextWeek')}
            disabled={!bothDates}
            onClick={() => shift(7)}
          />
          <TaskStatusSelect value={filters.status} onChange={(status) => set({ status })} />
          <FilterSelect
            label={t('tasks.fields.assignee')}
            allLabel={t('tasks.allAssignees')}
            options={(resources.data ?? []).map((r) => ({ value: r.id, label: r.name }))}
            value={filters.assigneeId}
            onChange={(assigneeId) => set({ assigneeId })}
          />
        </div>
        {tasks.data && <TaskSummaryLine summary={tasks.data.summary} />}
        <TasksTable
          label={t('tasks.title')}
          rows={tasks.data?.rows}
          loading={tasks.isFetching}
          columns={['project', 'title', 'assignee', 'plan', 'fact', 'deadline', 'done', 'actions']}
          scope={scope}
          totals={tasks.data?.summary}
          empty={<EmptyState icon="list-checks" title={t('tasks.emptyFiltered')} />}
        />
        <ProjectsWithoutTasks />
      </div>
      <TaskDialog open={adding} task={null} scope={scope} onClose={() => setAdding(false)} />
    </>
  );
}

/** "⚠️ Projekti bez uzdevumiem" — open projects with no task at all; hidden when none (FR-TASK-019). */
function ProjectsWithoutTasks() {
  const { t } = useTranslation();
  const projects = useApiQuery(routes.projects.withoutTasks);
  if (!projects.data?.length) return null;
  return (
    <Card title={t('tasks.withoutProjects')}>
      <ul aria-label={t('tasks.withoutProjects')} className="flex flex-wrap gap-2">
        {projects.data.map((p) => (
          <li key={p.id}>
            <Link
              to={`/projects/${p.id}`}
              className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-sm hover:border-brand-200"
            >
              {p.name}
              <ProjectStatusBadge status={p.status} />
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}
